import { ChildDto, UserDto } from '@ecdlink/core';
import {
  AddChildCaregiverTokenModelInput,
  AddChildLearnerTokenModelInput,
  AddChildRegistrationTokenModelInput,
  AddChildSiteAddressTokenModelInput,
  AddChildTokenModelInput,
  AddChildUserConsentTokenModelInput,
  ChildInput,
  UserModelInput,
  WorkflowStatusEnum,
} from '@ecdlink/graphql';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ChildRegistrationDetails } from '../../pages/child/caregiver-child-registration/caregiver-child-registration.types';
import { ChildService } from '@services/ChildService';
import { UserService } from '@services/UserService';
import { RootState, ThunkApiType } from '../types';
import { documentThunkActions } from '@store/document';
import { classroomsThunkActions } from '@store/classroom';
import { userThunkActions } from '@store/user';
import { PointsService } from '@/services/PointsService';
import { childrenActions } from './children';

export const getChildren = createAsyncThunk<
  ChildDto[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  'getChildren',
  // eslint-disable-next-line no-empty-pattern
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      children: { children: childrenCache },
    } = getState();

    if (!childrenCache) {
      try {
        let children: ChildDto[] | undefined;

        if (userAuth?.auth_token) {
          children = await new ChildService(userAuth?.auth_token).getChildren();
        } else {
          return rejectWithValue('no access token, profile check required');
        }

        if (!children) {
          return rejectWithValue('Error getting Children');
        }

        return children;
      } catch (err) {
        return rejectWithValue(err);
      }
    } else {
      return childrenCache;
    }
  }
);

type updateLearnerHierarchyRequest = {
  practitionerId: string;
  learnerId: string;
};

export const updateLearnerHierarchy = createAsyncThunk<
  boolean,
  updateLearnerHierarchyRequest,
  ThunkApiType<RootState>
>(
  'updateLearnerHierarchy',
  async ({ practitionerId, learnerId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        const result = await new ChildService(
          userAuth?.auth_token
        ).updateLearnerHierarchy(practitionerId, learnerId);
        return result;
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

type CreateChildRequest = {
  child: ChildDto;
};

export const createChild = createAsyncThunk<
  ChildDto,
  CreateChildRequest,
  ThunkApiType<RootState>
>('createChild', async ({ child }, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userAuth },
    } = getState();
    if (userAuth?.auth_token) {
      const input = mapChildInput(child);
      await new ChildService(userAuth?.auth_token).updateChild(
        input.Id || '',
        input
      );
      return child;
    } else return rejectWithValue('no access token, profile check required');
  } catch (err) {
    return rejectWithValue(err);
  }
});

type UpdateChildRequest = {
  child: ChildDto;
  id: string;
};

export const updateChild = createAsyncThunk<
  ChildDto,
  UpdateChildRequest,
  ThunkApiType<RootState>
>('updateChild', async ({ child }, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userAuth },
    } = getState();
    if (userAuth?.auth_token) {
      const input = mapChildInput(child);
      await new ChildService(userAuth?.auth_token).updateChild(
        input.Id || '',
        input
      );
      return child;
    } else return rejectWithValue('no access token, profile check required');
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const calculateChildrenRegistrationRemoval = createAsyncThunk<
  boolean,
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  'calculateChildrenRegistrationRemoval',
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        return await new ChildService(
          userAuth?.auth_token
        ).calculateChildrenRegistrationRemoval(userAuth?.id);
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const checkAndCreateChildUsers = createAsyncThunk<
  boolean[],
  {},
  ThunkApiType<RootState>
>('checkAndCreateChildUsers', async ({}, { getState, rejectWithValue }) => {
  const {
    auth: { userAuth },
    children: { childUser },
  } = getState();
  try {
    let promises: Promise<boolean>[] = [];

    if (userAuth?.auth_token && childUser) {
      const userService = new UserService(userAuth.auth_token);

      promises = childUser
        ?.filter((item) => !item?.synced)
        .map(async (x) => {
          try {
            const existingUser = await userService.doesUserExist(x.id ?? '');

            if (existingUser || x.isActive === false) {
              return true;
            }

            const input: UserModelInput = {
              id: x.id,
              isSouthAfricanCitizen: x.isSouthAfricanCitizen ?? false,
              idNumber: x.idNumber && x.idNumber.length > 0 ? x.idNumber : null,
              verifiedByHomeAffairs: x.verifiedByHomeAffairs!,
              dateOfBirth: x.dateOfBirth,
              genderId: x.genderId && x.genderId.length > 0 ? x.genderId : null,
              raceId: x.raceId && x.raceId.length > 0 ? x.raceId : null,
              firstName: x.firstName,
              surname: x.surname,
              contactPreference: x.contactPreference,
              phoneNumber: x.phoneNumber,
              email: x.email && x.email.length > 0 ? x.email : null,
              profileImageUrl: x.profileImageUrl,
            };

            const result = await userService.addUser(input);
            return !!result;
          } catch (err) {
            console.log(`Error processing child user ${x.id}:` + err);
            return false;
          }
        });
    }

    return Promise.all(promises);
  } catch (err) {
    return rejectWithValue(err);
  }
});

type UpdateChildUserRequest = {
  childUser: UserDto;
  id: string;
};

export const updateChildUser = createAsyncThunk<
  UserDto,
  UpdateChildUserRequest,
  ThunkApiType<RootState>
>(
  'updateChildUser',
  async ({ childUser, id }, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userAuth },
      } = getState();
      if (userAuth?.auth_token) {
        const input = mapUserInput(childUser);
        await new UserService(userAuth?.auth_token).updateUser(id, input);
        return childUser;
      } else return rejectWithValue('no access token, profile check required');
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const upsertChildUsers = createAsyncThunk<
  boolean[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  'upsertChildUsers',
  // eslint-disable-next-line no-empty-pattern
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      children: { childUser, children },
      staticData: { WorkflowStatuses },
    } = getState();

    try {
      let promises: Promise<boolean>[] = [];
      const workflowStatus = WorkflowStatuses?.find(
        (x) => x.enumId === WorkflowStatusEnum.ChildExternalLink
      );
      if (userAuth?.auth_token && childUser) {
        promises = childUser
          .filter((item) => !item?.synced)
          .filter((childUser) => {
            const child = children?.find((x) => x.userId === childUser.id);
            return child &&
              child.isActive &&
              child.workflowStatusId !== workflowStatus?.id
              ? true
              : false;
          })
          .map(async (x) => {
            const input: UserModelInput = {
              isSouthAfricanCitizen: x.isSouthAfricanCitizen ?? false,
              idNumber: x.idNumber && x.idNumber.length > 0 ? x.idNumber : null,
              verifiedByHomeAffairs: x.verifiedByHomeAffairs!,
              dateOfBirth: x.dateOfBirth,
              genderId: x.genderId && x.genderId.length > 0 ? x.genderId : null,
              raceId: x.raceId && x.raceId.length > 0 ? x.raceId : null,
              firstName: x.firstName,
              surname: x.surname,
              contactPreference: x.contactPreference,
              phoneNumber: x.phoneNumber,
              email: x.email,
              profileImageUrl: x.profileImageUrl,
            };

            return await new UserService(userAuth?.auth_token).updateUser(
              x.id ?? '',
              input
            );
          });
      }
      return Promise.all(promises);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const checkAndCreateChildren = createAsyncThunk<
  boolean[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  'checkAndCreateChildren',
  async ({}, { getState, rejectWithValue, dispatch }) => {
    const {
      auth: { userAuth },
      children: { children },
      staticData: { WorkflowStatuses },
      documents: { documents },
      classroomData: { classroomGroups, classroomGroupLearners },
      practitioner: { practitioner },
      user: { userConsent },
    } = getState();
    try {
      let promises: Promise<boolean>[] = [];
      const workflowStatus = WorkflowStatuses?.find(
        (x) => x.enumId === WorkflowStatusEnum.ChildExternalLink
      );

      // The API files a child under a practitioner by user id, not by
      // practitioner id. Only used when the child has no class group to
      // inherit the owning practitioner from.
      const fallbackPractitionerUserId = practitioner?.userId;

      if (userAuth?.auth_token && children) {
        const childService = new ChildService(userAuth.auth_token);
        const pointsService = new PointsService(userAuth.auth_token);

        promises = children
          .filter((item) => !item?.synced)
          .filter((child) => child.workflowStatusId !== workflowStatus?.id)
          .map(async (x) => {
            try {
              const existingChild = await childService.getChildByChildId(
                x.id ?? ''
              );

              if (x.isActive === false) {
                return true;
              }

              const input: ChildInput = {
                Id: x.id,
                UserId: x.userId,
                LanguageId: x.languageId,
                CaregiverId: x.caregiverId || null,
                Allergies: x.allergies,
                Disabilities: x.disabilities,
                OtherHealthConditions: x.otherHealthConditions,
                WorkflowStatusId: x.workflowStatusId,
                IsActive: true,
              };

              // A child that reached the server on an earlier, partly
              // completed sync still needs its class assignment replayed.
              const result = existingChild
                ? true
                : await childService.createChild(input);

              if (result && !existingChild) {
                await pointsService.addChildRegistrationPoints();
              }

              if (result && !existingChild && documents) {
                const childDocuments = documents.filter(
                  (doc) => doc.userId === x.userId
                );
                await Promise.all(
                  childDocuments.map(
                    async (doc) =>
                      await dispatch(
                        documentThunkActions.createDocument({ document: doc })
                      )
                  )
                );
              }

              const childLearners =
                result && classroomGroupLearners
                  ? classroomGroupLearners.filter(
                      (learner) => learner.userId === x.userId
                    )
                  : [];

              if (childLearners.length) {
                await Promise.all(
                  childLearners.map(async (learner) => {
                    await dispatch(
                      classroomsThunkActions.createLearner({ learner })
                    );
                  })
                );
              }

              if (result && !existingChild && userConsent) {
                const consent = userConsent.filter(
                  (c) => c.userId === x.userId
                );
                await Promise.all(
                  consent.map(async (c) => {
                    await dispatch(userThunkActions.upsertUserConsents(c));
                  })
                );
              }

              // File the child under the practitioner who owns the class group
              // they were assigned to - the assigned classroom teacher, who is
              // not necessarily the practitioner running the sync. Without
              // this the child keeps the hierarchy it was created under (the
              // principal's) and never shows up on the teacher's profile.
              const assignedPractitionerUserIds = childLearners
                .map(
                  (learner) =>
                    classroomGroups?.find(
                      (group) => group.id === learner.classroomGroupId
                    )?.userId
                )
                .filter((userId): userId is string => !!userId);

              const hierarchyPractitionerUserIds = Array.from(
                new Set(
                  assignedPractitionerUserIds.length
                    ? assignedPractitionerUserIds
                    : fallbackPractitionerUserId
                    ? [fallbackPractitionerUserId]
                    : []
                )
              );

              for (const practitionerUserId of hierarchyPractitionerUserIds) {
                await dispatch(
                  updateLearnerHierarchy({
                    practitionerId: practitionerUserId,
                    learnerId: x.userId || '',
                  })
                ).unwrap();
              }

              return !!result;
            } catch (err) {
              console.log(`Error processing child ${x.id}:` + err);
              return false;
            }
          });
      }

      return Promise.all(promises);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const upsertChildren = createAsyncThunk<
  boolean[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  'upsertChildren',
  // eslint-disable-next-line no-empty-pattern
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      children: { children },
      staticData: { WorkflowStatuses },
    } = getState();

    try {
      let promises: Promise<boolean>[] = [];
      const workflowStatus = WorkflowStatuses?.find(
        (x) => x.enumId === WorkflowStatusEnum.ChildExternalLink
      );
      if (userAuth?.auth_token && children) {
        promises = children
          .filter((item) => !item?.synced)
          .filter(
            (child) =>
              child.isActive && child.workflowStatusId !== workflowStatus?.id
          )
          .map(async (x) => {
            const input: ChildInput = {
              Id: x.id,
              UserId: x.userId,
              LanguageId: x.languageId,
              CaregiverId: x.caregiverId || null,
              Allergies: x.allergies,
              Disabilities: x.disabilities,
              OtherHealthConditions: x.otherHealthConditions,
              WorkflowStatusId: x.workflowStatusId,
              IsActive: x.isActive === false ? false : true,
            };

            if (x?.isOnline === false) {
              return await new ChildService(userAuth?.auth_token).updateChild(
                x.id ?? '',
                input
              );
            }
            return true;
          });
      }
      return Promise.all(promises);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateOfflineDeactivatedChildren = createAsyncThunk<
  boolean[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  'updateOfflineDeactivatedChildren',
  // eslint-disable-next-line no-empty-pattern
  async ({}, { getState, rejectWithValue, dispatch }) => {
    const {
      auth: { userAuth },
      children: { children },
    } = getState();

    try {
      const syncedResults: boolean[] = [];

      if (userAuth?.auth_token && children) {
        // Any child deactivated locally and not yet synced still needs the
        // deactivation pushed. upsertChildren skips inactive children, so a
        // child that lost the isOfflineDeactivation flag would otherwise never
        // have its deactivation sent by any step, leaving it active on the
        // backend and still visible on every linked practitioner's profile.
        const offlineDeactivatedChildren = children.filter(
          (child) =>
            child.isActive === false &&
            (child.isOfflineDeactivation === true || child.synced === false)
        );

        for (const child of offlineDeactivatedChildren) {
          const input: ChildInput = {
            Id: child.id,
            UserId: child.userId,
            LanguageId: child.languageId,
            CaregiverId: child.caregiverId || null,
            Allergies: child.allergies,
            Disabilities: child.disabilities,
            OtherHealthConditions: child.otherHealthConditions,
            WorkflowStatusId: child.workflowStatusId,
            IsActive: false,
            InactiveDate: child.inactiveDate,
            InactivityComments: child.inactivityComments,
            ReasonForLeavingId: child.reasonForLeavingId,
          };

          // Keep going if one child fails. This is the last sync step, so
          // letting a single failure throw would strand every remaining
          // deactivation behind it.
          try {
            const result = await new ChildService(
              userAuth?.auth_token
            ).updateChild(child.id ?? '', input);

            if (result) {
              dispatch(
                childrenActions.markOfflineDeactivatedChildSynced({
                  childId: child.id ?? '',
                })
              );
            }

            syncedResults.push(result);
          } catch (err) {
            console.error(
              `Error syncing offline deactivation for child ${child.id}:`,
              err
            );
            syncedResults.push(false);
          }
        }

        await new ChildService(
          userAuth?.auth_token
        ).calculateChildrenRegistrationRemoval(userAuth?.id);

        return syncedResults;
      }

      return [];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

type GenerateCaregiverChildTokenRequest = {
  firstName?: string;
  surname?: string;
  classgroupId?: string;
};

export const generateCaregiverChildToken = createAsyncThunk<
  string,
  // eslint-disable-next-line @typescript-eslint/ban-types
  GenerateCaregiverChildTokenRequest,
  ThunkApiType<RootState>
>(
  'generateCaregiverChildToken',
  // eslint-disable-next-line no-empty-pattern
  async (
    { firstName, surname, classgroupId },
    { getState, rejectWithValue }
  ) => {
    try {
      const {
        auth: { userAuth },
      } = getState();
      if (userAuth?.auth_token) {
        const result = await new ChildService(
          userAuth?.auth_token
        ).generateCaregiverChildToken(
          firstName || '',
          surname || '',
          classgroupId || ''
        );
        return result;
      } else return rejectWithValue('no access token, profile check required');
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

type RefreshCaregiverChildTokenRequest = {
  childId?: string;
  classgroupId?: string;
};

export const refreshCaregiverChildToken = createAsyncThunk<
  string,
  // eslint-disable-next-line @typescript-eslint/ban-types
  RefreshCaregiverChildTokenRequest,
  ThunkApiType<RootState>
>(
  'refreshCaregiverChildToken',
  // eslint-disable-next-line no-empty-pattern
  async ({ classgroupId, childId }, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userAuth },
      } = getState();
      if (userAuth?.auth_token) {
        const result = await new ChildService(
          userAuth?.auth_token
        ).refreshCaregiverChildToken(childId || '', classgroupId || '');
        return result;
      } else return rejectWithValue('no access token, profile check required');
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

type OpenAccessAddChildDetailRequest = {
  token?: string;
};

export const openAccessAddChildDetail = createAsyncThunk<
  ChildRegistrationDetails,
  // eslint-disable-next-line @typescript-eslint/ban-types
  OpenAccessAddChildDetailRequest,
  ThunkApiType<RootState>
>(
  'openAccessAddChildDetail',
  // eslint-disable-next-line no-empty-pattern
  async ({ token }, { rejectWithValue }) => {
    try {
      const result = await new ChildService('').openAccessAddChildDetail(
        token || ''
      );
      return result;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export type TokenAddChildRequest = {
  token: string;
  caregiver: AddChildCaregiverTokenModelInput;
  learner: AddChildLearnerTokenModelInput;
  siteAddress: AddChildSiteAddressTokenModelInput;
  child: AddChildTokenModelInput;
  registration?: AddChildRegistrationTokenModelInput;
  userConsent?: AddChildUserConsentTokenModelInput;
};

export const openAccessAddChild = createAsyncThunk<
  string,
  // eslint-disable-next-line @typescript-eslint/ban-types
  TokenAddChildRequest,
  ThunkApiType<RootState>
>(
  'openAccessAddChild',
  // eslint-disable-next-line no-empty-pattern
  async (
    {
      token,
      caregiver,
      learner,
      siteAddress,
      child,
      registration,
      userConsent,
    },
    { rejectWithValue }
  ) => {
    try {
      const result = await new ChildService('').openAccessAddChild(
        token,
        caregiver,
        learner,
        siteAddress,
        child,
        registration,
        userConsent
      );
      return result;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const mapChildInput = (child: Partial<ChildDto>): ChildInput => ({
  Id: child.id,
  UserId: child.userId,
  LanguageId: child.languageId,
  CaregiverId: child.caregiverId || null,
  Allergies: child.allergies,
  Disabilities: child.disabilities,
  OtherHealthConditions: child.otherHealthConditions,
  WorkflowStatusId: child.workflowStatusId,
  IsActive: child.isActive === false ? false : true,
  InsertedBy: child?.insertedBy,
  ReasonForLeavingId: child?.reasonForLeavingId || null,
  InactivityComments: child?.inactivityComments || null,
  InactiveDate: child?.inactiveDate || null,
});

const mapUserInput = (child: Partial<UserDto>): UserModelInput => ({
  id: child.id,
  isSouthAfricanCitizen: child.isSouthAfricanCitizen ?? false,
  idNumber: child.idNumber && child.idNumber.length > 0 ? child.idNumber : null,
  verifiedByHomeAffairs: child.verifiedByHomeAffairs || false,
  dateOfBirth: child.dateOfBirth,
  genderId: child.genderId && child.genderId.length > 0 ? child.genderId : null,
  raceId: child.raceId && child.raceId.length > 0 ? child.raceId : null,
  firstName: child.firstName,
  surname: child.surname,
  contactPreference: child.contactPreference,
  phoneNumber: child.phoneNumber,
  email: child.email,
  profileImageUrl: child.profileImageUrl,
});
export const getChildrenForCoach = createAsyncThunk<
  ChildDto[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  'getChildrenForCoach',
  // eslint-disable-next-line no-empty-pattern
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      children: { children: childrenCache },
    } = getState();

    if (!childrenCache) {
      try {
        let children: ChildDto[] | undefined;

        if (userAuth?.auth_token) {
          children = await new ChildService(
            userAuth?.auth_token
          ).getChildrenForCoachV2();
        } else {
          return rejectWithValue('no access token, profile check required');
        }

        return children;
      } catch (err) {
        return rejectWithValue(err);
      }
    } else {
      return childrenCache;
    }
  }
);
